import { computed, watch, onUnmounted } from 'vue'

/**
 * Composable for keyboard navigation functionality
 * @param {Object} options - Configuration options
 * @param {Ref<number>} options.currentIndex - Current active index
 * @param {Ref<number>} options.maxIndex - Maximum index value
 * @param {Ref<boolean>} options.isActive - Whether navigation is active
 * @param {Object} options.keyMap - Custom key mappings (optional)
 * @returns {Object} Navigation methods and state
 */
export function useKeyboardNavigation({ currentIndex, maxIndex, isActive, keyMap = {} }) {
  const defaultKeyMap = {
    Escape: 'close',
    ArrowLeft: 'prev',
    ArrowRight: 'next'
  }

  const mergedKeyMap = { ...defaultKeyMap, ...keyMap }

  // Computed properties for navigation bounds
  const canNavigateLeft = computed(() => currentIndex.value > 0)
  const canNavigateRight = computed(() => currentIndex.value < maxIndex.value)

  // Navigation methods
  const navigateLeft = () => {
    if (!canNavigateLeft.value) return
    currentIndex.value--
  }

  const navigateRight = () => {
    if (!canNavigateRight.value) return
    currentIndex.value++
  }

  const close = () => {
    currentIndex.value = null
  }

  // Keyboard event handler
  const handleKeyEvent = (ev) => {
    if (!isActive.value) return

    const keyActions = {
      close,
      prev: navigateLeft,
      next: navigateRight
    }

    const actionKey = mergedKeyMap[ev.key]
    const action = keyActions[actionKey]

    if (action) {
      ev.preventDefault()
      action()
    }
  }

  // Event listener management
  const addKeyboardListener = () => {
    window.addEventListener('keydown', handleKeyEvent)
  }

  const removeKeyboardListener = () => {
    window.removeEventListener('keydown', handleKeyEvent)
  }

  // Watch for active state changes
  watch(
    isActive,
    (isActive) => {
      if (isActive) {
        addKeyboardListener()
      } else {
        removeKeyboardListener()
      }
    },
    { immediate: true }
  )

  // Cleanup on unmount
  onUnmounted(() => {
    removeKeyboardListener()
  })

  return {
    canNavigateLeft,
    canNavigateRight,
    navigateLeft,
    navigateRight,
    close,
    addKeyboardListener,
    removeKeyboardListener
  }
}
